import { IlocalStorageProvider } from "../../adapters/driven/providers/localStorageProvider";
import { IClinicRepository } from "../../adapters/driven/repositories/clinicRepository";

export const ClinicServiceImplFactory = ({
  clinicRepository,
  localStorageProvider,
  notificationService,
}: {
  clinicRepository: IClinicRepository;
  localStorageProvider: IlocalStorageProvider;
  notificationService: INotificationService;
}) => {
  const getMyClinics: IGetMyClinic = async () => {
    try {
      const response = await clinicRepository.getClinicID();
      let sortedClinicList: IClinic[] = [];
      if (response) {
        sortedClinicList = [...response];
        sortedClinicList = sortedClinicList
          .filter((clinic) => clinic.name.length)
          .sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
      }
      return {
        success: true,
        data: sortedClinicList,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const setSelectedClinic: ISetSelectedClinic = async ({
    id,
    clinicList,
    notificationSettings,
  }) => {
    await notificationService.setNotificationSetting(notificationSettings);
    return clinicList.find((item) => item.id === id) || null;
  };

  const getSelectedClinic: IGetSelectedClinic = ({ clinicList }) => {
    const selectedClinic = localStorageProvider.get("selectedClinic");

    if (selectedClinic) {
      return (
        clinicList.find((item) => item.id === Number(selectedClinic)) || null
      );
    }
    return null;
  };

  const setDefaultClinic: IGetSelectedClinic = ({ clinicList }) => {
    const clinic = clinicList[0];
    if (clinic) {
      localStorageProvider.set("selectedClinic", clinic.id.toString());
      return clinic;
    }
    return null;
  };

  const getClinicFloor: IGetClinicFloor = async ({ clinicId }) => {
    try {
      const response = await clinicRepository.getClinicFloor({
        clinic_id: clinicId,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const setClinicFloor: ISetClinicFloor = async (obj) => {
    try {
      const response = await clinicRepository.setClinicFloor({
        clinic_id: obj.clinicId,
        floor_ids: obj.floor_ids,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const logError: ILogError = async (obj) => {
    try {
      const response = await clinicRepository.logError(obj);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const updateFacilityInformation: IUpdateFacilityInformation = async (obj) => {
    try {
      const response = await clinicRepository.updateFacilityInformation(obj);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const getClinicContactInfo: IGetClinicContactInfo = async (obj) => {
    try {
      return {
        success: true,
        data: await clinicRepository.getClinicContactInfo(obj),
      };
    } catch (error) {
      return { success: false, error };
    }
  };

  const replaceClinicContactInfo: IReplaceClinicContactInfo = async (obj) => {
    try {
      return {
        success: true,
        data: await clinicRepository.replaceClinicContactInfo(obj),
      };
    } catch (error) {
      return { success: false, error };
    }
  };

  const patchClinicContactInfo: IPatchClinicContactInfo = async (obj) => {
    try {
      return {
        success: true,
        data: await clinicRepository.patchClinicContactInfo(obj),
      };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getPsnSettings: IGetPsnSettings = async ({ clinic_id }) => {
    try {
      const response = await clinicRepository.getPsnSettings({ clinic_id });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const patchPsnSettings: IPatchPsnSettings = async (obj) => {
    try {
      const { clinic_id, ...body } = obj;
      const response = await clinicRepository.patchPsnSettings({
        clinic_id,
        ...body,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const getClinicConfig: IGetClinicConfig = async ({ clinic_id }) => {
    try {
      const response = await clinicRepository.getClinicConfig({ clinic_id });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const patchClinicConfig: IPatchClinicConfig = async (obj) => {
    try {
      const response = await clinicRepository.patchClinicConfig(obj);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const getClinicShiftReportConfig: IGetClinicShiftReportConfig = async ({
    clinic_id,
  }) => {
    try {
      const response = await clinicRepository.getClinicShiftReportConfig({
        clinic_id,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  return {
    getClinicFloor,
    getMyClinics,
    getSelectedClinic,
    setDefaultClinic,
    setSelectedClinic,
    setClinicFloor,
    logError,
    updateFacilityInformation,
    getClinicContactInfo,
    replaceClinicContactInfo,
    patchClinicContactInfo,
    getPsnSettings,
    patchPsnSettings,
    getClinicConfig,
    patchClinicConfig,
    getClinicShiftReportConfig,
  };
};
